import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Select,
  Input,
  Tag,
  Dropdown,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FilterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
  DownOutlined,
  PrinterOutlined,
  ReloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";

import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";

import {
  HEADERS_EXPORT_BEN,
  POST_LIST_FIELD_COLLECTION,
} from "../../API/apiUrls";
import RequestDocModal from "./modal/RequestDocModal";
import AddContractModal from "./modal/AddContractModal";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";

const { Text } = Typography;

const OPTIONS_DOC_STATUS = [
  { value: "all", label: "ทั้งหมด" },
  { value: "enforce", label: "มีหมายบังคับคดี" },
  { value: "seize", label: "หมายคดีตั้ง" },
  { value: "fully", label: "เอกสารครบ" },
  { value: "none", label: "ยังไม่มีเอกสาร" },
];

const getCaseTypeLabel = (r) => {
  if (r.flagLoanNew === 1) return "ทำยอม";
  if (r.judgeType) return r.judgeType;
  return "ยังไม่มีคำพิพากษา";
};

// ── แปลข้อความสถานะดิบจากระบบให้เป็นคำที่ผู้ใช้เข้าใจง่าย ──────
const STATUS_DESCRIPTION_LABELS = {
  "ceo อนุมัติ": "เกิดสัญญาใหม่แล้ว",
};

const getStatusDescriptionLabel = (statusDescription) =>
  STATUS_DESCRIPTION_LABELS[statusDescription] || statusDescription;

const formatThaiDate = (d) =>
  d ? dayjs(d).add(543, "year").format("DD/MM/YYYY") : "-";

// วันที่รูปแบบ export ตามตัวอย่าง (2026-03-05)
const formatDateExport = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "-");

const Main = () => {
  const roleId = parseInt(localStorage.getItem("ROLE_ID"));
  const [signOut] = TokenCheck();

  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [activeFilter, setActiveFilter] = useState("all");
  const [searchContno, setSearchContno] = useState("");
  const [lawyerName, setLawyerName] = useState(undefined);
  const [caseTypeFilter, setCaseTypeFilter] = useState(undefined);
  const [docStatusFilter, setDocStatusFilter] = useState("all");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [dataRecord, setDataRecord] = useState(null);
  const [isModal, setIsModal] = useState(false);
  const [isAddModal, setIsAddModal] = useState(false);

  const getRowKey = useCallback((r) => r.id, []);

  const lawyerOptions = useMemo(() => {
    const countByLawyer = new Map();
    arrayPay.forEach((r) => {
      if (!r.lawyerName) return;
      countByLawyer.set(
        r.lawyerName,
        (countByLawyer.get(r.lawyerName) || 0) + 1,
      );
    });
    return Array.from(countByLawyer.entries())
      .sort(([a], [b]) => a.localeCompare(b, "th"))
      .map(([name, count]) => ({ value: name, label: `${name} (${count})` }));
  }, [arrayPay]);

  const caseTypeOptions = useMemo(() => {
    const countByType = new Map();
    arrayPay.forEach((r) => {
      const label = getCaseTypeLabel(r);
      countByType.set(label, (countByType.get(label) || 0) + 1);
    });
    return Array.from(countByType.entries())
      .sort(([a], [b]) => a.localeCompare(b, "th"))
      .map(([label, count]) => ({
        value: label,
        label: `${label} (${count})`,
      }));
  }, [arrayPay]);

  // ── Query ─────────────────────────────────────────────
  const queryData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_LIST_FIELD_COLLECTION,
        { userId: 1 },
        { headers: HEADERS_EXPORT_BEN },
      );
      const data = Array.isArray(res.data.data) ? res.data.data : [];

      setArrayPay(data);
      setFilteredData(data);
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setArrayPay([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  useEffect(() => {
    queryData();
  }, []);

  // ── Filter (มีผลเฉพาะตอนกดปุ่ม "กรองข้อมูล") ──────────
  const handleFilter = useCallback(() => {
    let filtered = arrayPay;

    if (activeFilter !== "all") {
      filtered = filtered.filter((d) =>
        activeFilter === "active" ? d.flagActive : !d.flagActive,
      );
    }
    if (lawyerName) {
      filtered = filtered.filter((d) => d.lawyerName === lawyerName);
    }
    if (caseTypeFilter) {
      filtered = filtered.filter((d) => getCaseTypeLabel(d) === caseTypeFilter);
    }
    if (docStatusFilter !== "all") {
      filtered = filtered.filter((d) => {
        if (docStatusFilter === "enforce") return !!d.docEnforce;
        if (docStatusFilter === "seize") return !!d.docSeize;
        return !d.docEnforce && !d.docSeize;
      });
    }

    const query = searchContno.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (d) =>
          d.contno?.toLowerCase().includes(query) ||
          d.newContno?.toLowerCase().includes(query) ||
          d.redCaseNumber?.toLowerCase().includes(query) ||
          d.blackCaseNumber?.toLowerCase().includes(query),
      );
    }

    setFilteredData(filtered);
    setSelectedRowKeys([]);
  }, [
    arrayPay,
    activeFilter,
    lawyerName,
    caseTypeFilter,
    docStatusFilter,
    searchContno,
  ]);

  const hasActiveFilters =
    activeFilter !== "all" ||
    !!lawyerName ||
    !!caseTypeFilter ||
    docStatusFilter !== "all" ||
    !!searchContno.trim();

  const handleResetFilters = useCallback(() => {
    setActiveFilter("all");
    setSearchContno("");
    setLawyerName(undefined);
    setCaseTypeFilter(undefined);
    setDocStatusFilter("all");
    setFilteredData(arrayPay);
    setSelectedRowKeys([]);
  }, [arrayPay]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 50,
      render: (_, __, i) => i + 1,
    },
    {
      title: "สัญญา",
      key: "contract",
      align: "left",
      width: 200,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <Text copyable={!!r.contno} strong>
              {r.contno || "-"}
            </Text>
          </div>
          {r.newContno && (
            <div style={{ color: "#888" }}>สัญญาใหม่ {r.newContno}</div>
          )}
          <div style={{ color: "#888" }}>เจ้าของงาน {r.ownerName || "-"}</div>
        </div>
      ),
    },
    {
      title: "คดี / ทนายความ",
      key: "case",
      align: "left",
      width: 210,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <span style={{ color: "#888" }}>เลขคดีดำ </span>
            {r.blackCaseNumber || "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>เลขคดีแดง </span>
            {r.redCaseNumber || "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>ทนายความ </span>
            {r.lawyerName || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "ประเภท / สถานะงาน",
      key: "stage",
      align: "left",
      width: 260,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <Tag color={r.flagLoanNew === 1 ? "gold" : "blue"}>
            {getCaseTypeLabel(r)}
          </Tag>
          <div style={{ color: "#595959", marginTop: 4 }}>{r.stage || "-"}</div>
          {r.statusDescription && (
            <div style={{ color: "#888" }}>
              สถานะ: {getStatusDescriptionLabel(r.statusDescription)}
            </div>
          )}
        </div>
      ),
    },
    //     export const OPTIONS_STATUS = [ //สถานะทรัพย์
    //   { value: 0, label: "ยังไม่เคยสืบทรัพย์" },
    //   { value: 1, label: "แจ้งธรุการส่งสืบทรัพย์" },
    //   { value: 2, label: "ดำเนินการส่งสืบทรัพย์" },
    //   { value: 3, label: "เจอทรัพย์" },
    //   { value: 4, label: "ไม่เจอทรัพย์" },
    // ];
    // {
    //   title: "สืบทรัพย์",
    //   key: "investigate",
    //   align: "left",
    //   width: 180,
    //   render: (_, r) => (
    //     <div style={{ fontSize: 13, lineHeight: 1.7 }}>
    //       <div style={{ color: "#888" }}>
    //         ส่งสืบ: {formatThaiDate(r.investigateSendDate)}
    //       </div>
    //       <div style={{ color: "#888" }}>
    //         ตอบกลับ: {formatThaiDate(r.investigateResponseDate)}
    //       </div>
    //       {r.investigateStatus != null && (
    //         <div style={{ color: "#888" }}>สถานะ: {r.investigateStatus}</div>
    //       )}
    //     </div>
    //   ),
    // },
    {
      title: "บังคับคดี",
      key: "enforce",
      align: "left",
      width: 240,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <Space size={4} wrap>
            <Tag color={r.docEnforce ? "green" : "default"}>หมายบังคับคดี</Tag>
            <Tag color={r.docSeize ? "green" : "default"}>หมายยึดทรัพย์</Tag>
          </Space>
          {r.docEnforceDate && (
            <div style={{ color: "#888" }}>
              หมายบังคับคดี: {formatThaiDate(r.docEnforceDate)}
            </div>
          )}
          {r.docSeizeDate && (
            <div style={{ color: "#888" }}>
              หมายยึดทรัพย์: {formatThaiDate(r.docSeizeDate)}
            </div>
          )}
          {r.enforceCaseDate && (
            <div style={{ color: "#888" }}>
              ตั้งเรื่อง: {formatThaiDate(r.enforceCaseDate)}
            </div>
          )}
          {r.impoundDate && (
            <div style={{ color: "#888" }}>
              ยึดทรัพย์: {formatThaiDate(r.impoundDate)}
            </div>
          )}
          {r.saleDate && (
            <div style={{ color: "#888" }}>
              ขายทอดตลาด: {formatThaiDate(r.saleDate)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "สถานะสัญญา",
      key: "contractStatus",
      align: "left",
      width: 200,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <Space size={4} wrap>
            <Tag color={r.followByName ? "blue" : "orange"}>
              {r.followByName ? "รับงานแล้ว" : "รอรับงาน"}
            </Tag>
          </Space>
          {r.followByName && (
            <div style={{ color: "#888" }}>
              ผู้รับ: {r.followByName}
              {r.acceptFollowDate && (
                <>
                  <br />
                  วันที่รับงาน: {formatThaiDate(r.acceptFollowDate)}
                </>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "การจัดการ",
      align: "center",
      width: 120,
      render: (_, r) => (
        <Button
          onClick={() => {
            setDataRecord(r);
            setIsModal(true);
          }}
        >
          ดูข้อมูล
        </Button>
      ),
    },
  ];

  // ✅ Reload หลังบันทึกสำเร็จ
  const handleReload = useCallback(() => {
    queryData();
  }, [queryData]);

  // ── Export helpers ────────────────────────────────────
  const selectedRows = useMemo(
    () => filteredData.filter((r) => selectedRowKeys.includes(getRowKey(r))),
    [filteredData, selectedRowKeys, getRowKey],
  );

  // ไม่ได้เลือกแถวใด ๆ → export ข้อมูลที่กรองไว้ทั้งหมดแทน
  const exportRows = selectedRows.length ? selectedRows : filteredData;

  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  const buildExportRows = () =>
    exportRows.map((r, i) => ({
      ลำดับ: i + 1,
      เลขสัญญา: r.contno || "",
      เลขที่ใหม่: r.newContno || "",
      เจ้าของงาน: r.ownerName || "",
      ทนายความ: r.lawyerName || "",
      เลขคดีดำ: r.blackCaseNumber || "",
      เลขคดีแดง: r.redCaseNumber || "",
      ประเภทคดี: getCaseTypeLabel(r),
      สถานะงาน: r.stage || "",
      สถานะปัจจุบัน: getStatusDescriptionLabel(r.statusDescription) || "",
      หมายบังคับคดี: r.docEnforce ? "มี" : "ไม่มี",
      หมายยึดทรัพย์: r.docSeize ? "มี" : "ไม่มี",
      วันที่ตั้งเรื่องบังคับคดี: formatDateExport(r.enforceCaseDate),
      วันที่ยึดทรัพย์: formatDateExport(r.impoundDate),
      วันที่ขายทอดตลาด: formatDateExport(r.saleDate),
      วันที่ส่งสืบ: formatDateExport(r.investigateSendDate),
      วันที่ตอบกลับสืบ: formatDateExport(r.investigateResponseDate),
      สถานะสัญญา: r.flagActive ? "ดำเนินการอยู่" : "จบเคส",
      วันที่ปิดสัญญา: formatDateExport(r.closedAt),
      วันที่สร้าง: formatDateExport(r.createdAt),
    }));

  // ✅ PDF เอาเฉพาะคอลัมน์สำคัญ (Excel ยังคงเอาข้อมูลทั้งหมด)
  const buildPdfExportRows = () =>
    exportRows.map((r, i) => ({
      ลำดับ: i + 1,
      เลขสัญญา: r.contno || "",
      เลขที่ใหม่: r.newContno || "",
      เจ้าของงาน: r.ownerName || "",
      ทนายความ: r.lawyerName || "",
      เลขคดีดำ: r.blackCaseNumber || "",
      เลขคดีแดง: r.redCaseNumber || "",
      ประเภทคดี: getCaseTypeLabel(r),
      สถานะงาน: r.stage || "",
      สถานะปัจจุบัน: getStatusDescriptionLabel(r.statusDescription) || "",
      หมายบังคับคดี: r.docEnforce ? "มี" : "ไม่มี",
      หมายยึดทรัพย์: r.docSeize ? "มี" : "ไม่มี",
    }));

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    const summaryRow = {
      ลำดับ: "",
      เลขสัญญา: "",
      เลขที่ใหม่: `รวมทั้งหมด ${rows.length} รายการ`,
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);
    const keys = Object.keys(rows[0]);

    const evenFill = { patternType: "solid", fgColor: { rgb: "EBF5FF" } };
    const oddFill = { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
    const summaryFill = { patternType: "solid", fgColor: { rgb: "D5E8D4" } };
    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    // header
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "344962" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderStyle,
      };
    });

    // data rows
    rows.forEach((row, ri) => {
      const fill = ri % 2 === 0 ? evenFill : oddFill;
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: { horizontal: "left", vertical: "center" },
          border: borderStyle,
        };
      });
    });

    // summary row
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: summaryFill,
        font: { bold: true, sz: 12 },
        alignment: { horizontal: "left", vertical: "center" },
        border: borderStyle,
      };
    });

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2, 12) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(
      wb,
      `รายงานสัญญาติดตามของชุดนอก_${dayjs().format("DDMMYYYY")}.xlsx`,
    );
    message.success("Export Excel สำเร็จ");
  };

  const handlePrintPDF = () => {
    if (!exportRows.length) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");

    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานสัญญาติดตามของชุดนอก", pageWidth / 2, 8, {
        align: "center",
      });
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(12);
      doc.text(`วันที่พิมพ์ ${now}`, 8, 16);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 16, { align: "right" });
    };

    const rows = buildPdfExportRows();
    const tableBody = rows.map((r) => Object.values(r));

    doc.autoTable({
      startY: 20,
      head: [Object.keys(rows[0])],
      body: tableBody,
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 10,
      },
      styles: {
        font: "THSarabunNew",
        fontSize: 9,
        cellPadding: 1,
        halign: "center",
      },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
      margin: { top: 20, left: 3, right: 3 },
    });

    doc.save(`รายงานสัญญาติดตามของชุดนอก_${dayjs().format("DDMMYYYY")}.pdf`);
  };

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>สัญญาติดตามของชุดนอก</Divider>

          {/* ── แถบกรองข้อมูล ── */}
          <Card
            size="small"
            title={
              <Space>
                <FilterOutlined />
                <span>ตัวกรองข้อมูล</span>
              </Space>
            }
            style={{ marginBottom: 12, background: "#fafafa" }}
          >
            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={12} md={6} lg={5}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ค้นหาเลขที่สัญญา / เลขคดี / ชื่อ-นามสกุล
                </Text>
                <Input
                  placeholder="เช่น 3-0001891"
                  value={searchContno}
                  onChange={(e) => setSearchContno(e.target.value)}
                  onPressEnter={handleFilter}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  allowClear
                  size="small"
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ทนายความ
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={lawyerOptions}
                  value={lawyerName}
                  onChange={setLawyerName}
                  allowClear
                  showSearch
                  placeholder="ทั้งหมด"
                  size="large"
                  disabled={roleId > 2}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ประเภทคดี
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={caseTypeOptions}
                  value={caseTypeFilter}
                  onChange={setCaseTypeFilter}
                  allowClear
                  placeholder="ทั้งหมด"
                  size="large"
                  popupMatchSelectWidth={false}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  เอกสารบังคับคดี
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={OPTIONS_DOC_STATUS}
                  value={docStatusFilter}
                  onChange={setDocStatusFilter}
                  size="large"
                  popupMatchSelectWidth={false}
                />
              </Col>

              <Col xs={24} lg="auto" style={{ textAlign: "right" }}>
                <Space wrap style={{ justifyContent: "flex-end" }}>
                  <Button
                    color="orange"
                    variant="outlined"
                    icon={<FilterOutlined />}
                    onClick={handleFilter}
                  >
                    กรองข้อมูล
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleResetFilters}
                    color="red"
                    variant="outlined"
                  >
                    ล้างตัวกรอง
                  </Button>
                  <Dropdown
                    menu={exportMenuItems}
                    disabled={!filteredData.length}
                  >
                    <Button icon={<PrinterOutlined />}>
                      Export <DownOutlined />
                    </Button>
                  </Dropdown>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* ── สรุปตัวกรองที่ใช้อยู่ ── */}
          {hasActiveFilters && (
            <Card
              size="small"
              style={{ background: "#f0f5ff", marginBottom: 12 }}
            >
              <Space split="|" size="large" wrap>
                <span>
                  <strong>ผลการค้นหา:</strong> {filteredData.length} รายการ
                </span>
                {activeFilter !== "all" && (
                  <span>
                    <strong>สถานะสัญญา:</strong>{" "}
                    {activeFilter === "active" ? "ดำเนินการอยู่" : "จบเคส"}
                  </span>
                )}
                {searchContno && (
                  <span>
                    <strong>ค้นหา:</strong> "{searchContno}"
                  </span>
                )}
                {lawyerName && (
                  <span>
                    <strong>ทนายความ:</strong> {lawyerName}
                  </span>
                )}
                {caseTypeFilter && (
                  <span>
                    <strong>ประเภทคดี:</strong> {caseTypeFilter}
                  </span>
                )}
                {docStatusFilter !== "all" && (
                  <span>
                    <strong>เอกสารบังคับคดี:</strong>{" "}
                    {OPTIONS_DOC_STATUS.find((o) => o.value === docStatusFilter)
                      ?.label || docStatusFilter}
                  </span>
                )}
              </Space>
            </Card>
          )}

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsAddModal(true)}
              style={{ borderRadius: 8 }}
            >
              เพิ่มสัญญา
            </Button>

            <Text
              strong
              style={{
                margin: "10px 0",
              }}
            >
              จำนวนสัญญาทั้งหมด : {arrayPay.length} เคส
              {selectedRowKeys.length > 0
                ? ` (เลือก ${selectedRowKeys.length} รายการ)`
                : " (ไม่เลือกรายการ = Export ข้อมูลทั้งหมด)"}
            </Text>
          </div>
          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={getRowKey}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
          />
        </Card>
        {isModal && (
          <RequestDocModal
            open={isModal}
            onCancel={setIsModal}
            dataDefault={dataRecord}
            onSuccess={handleReload}
          />
        )}
        {isAddModal && (
          <AddContractModal
            open={isAddModal}
            onCancel={setIsAddModal}
            onSuccess={handleReload}
            lawyerOptions={lawyerOptions}
          />
        )}
      </Spin>
    </>
  );
};

const MainContractToFieldCollection = MotionHoc(Main);
export default MainContractToFieldCollection;
